import { clsx } from "clsx";
import type { ElementType } from "react";
import { createElement } from "react";
import { Box, type BoxElement, type BoxProps } from "../Box/Box";
import type {
  AlignValue,
  Breakpoint,
  FlexDirection,
  ResponsiveAlign,
  ResponsiveDirection,
  ResponsiveSpace,
} from "../tokens";
import { gapBreakpointMap, gapMap, resolveResponsive } from "../tokens";

type FlexOwnProps = {
  direction?: ResponsiveDirection;
  gap?: ResponsiveSpace;
  align?: ResponsiveAlign;
  justify?: "start" | "center" | "end" | "between";
  wrap?: boolean;
  inline?: boolean;
  shrink?: boolean;
};

export type FlexProps<T extends BoxElement = "div"> = BoxProps<T> & FlexOwnProps;

const directionMap: Record<FlexDirection, string> = {
  row: "flex-row",
  column: "flex-col",
};

const directionBreakpointMap: Record<Breakpoint, Record<FlexDirection, string>> = {
  md: { row: "md:flex-row", column: "md:flex-col" },
  lg: { row: "lg:flex-row", column: "lg:flex-col" },
};

const alignMap: Record<AlignValue, string> = {
  start: "items-start",
  center: "items-center",
  end: "items-end",
  stretch: "items-stretch",
  baseline: "items-baseline",
};

const alignBreakpointMap: Record<Breakpoint, Record<AlignValue, string>> = {
  md: {
    start: "md:items-start",
    center: "md:items-center",
    end: "md:items-end",
    stretch: "md:items-stretch",
    baseline: "md:items-baseline",
  },
  lg: {
    start: "lg:items-start",
    center: "lg:items-center",
    end: "lg:items-end",
    stretch: "lg:items-stretch",
    baseline: "lg:items-baseline",
  },
};

const justifyMap = {
  start: "justify-start",
  center: "justify-center",
  end: "justify-end",
  between: "justify-between",
} as const;

export function Flex<T extends BoxElement = "div">({
  direction = "row",
  gap,
  align = "stretch",
  justify = "start",
  wrap = false,
  inline = false,
  shrink = true,
  className,
  ...boxProps
}: FlexProps<T>) {
  const classes = clsx(
    inline ? "inline-flex" : "flex",
    resolveResponsive(direction, directionMap, directionBreakpointMap),
    resolveResponsive(align, alignMap, alignBreakpointMap),
    justifyMap[justify],
    wrap && "flex-wrap",
    !shrink && "shrink-0",
    gap !== undefined && resolveResponsive(gap, gapMap, gapBreakpointMap),
    className,
  );

  return createElement(
    Box as unknown as ElementType,
    {
      "data-component": "Flex",
      ...boxProps,
      className: classes,
    } as unknown as BoxProps<T>,
  );
}

Flex.displayName = "Flex";
